import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { CreditCard, Plus, Edit, Trash2, X } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function DigitalCardsPage() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [cardCategories, setCardCategories] = useState([]);
  const [colours, setColours] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    short_description: '',
    card_category_id: '',
    available_colours: [],
    material_id: '',
    hero_image: null
  });
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [colorVariantImages, setColorVariantImages] = useState({});
  // Structure: { colour_id: { front: file/url, back: file/url, price: number } }

  useEffect(() => {
    checkAuth();
    fetchCards();
    fetchCardCategories();
    fetchColours();
    fetchMaterials();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchCardCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/card-categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setCardCategories(response.data.card_categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch card categories:', err);
    }
  };

  const fetchColours = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/colours`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setColours(response.data.colours || []);
      }
    } catch (err) {
      console.error('Failed to fetch colours:', err);
    }
  };

  const fetchMaterials = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/materials`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMaterials(response.data.materials || []);
      }
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/digital-cards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch digital cards');
      }

      setCards(response.data.cards || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [`${type}_image`]: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'front') {
          setFrontImagePreview(reader.result);
        } else {
          setBackImagePreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      
      // Step 1: Create/Update card with basic info and color structure
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('short_description', formData.short_description);
      
      if (formData.card_category_id) {
        formDataToSend.append('card_category_id', formData.card_category_id);
      }
      
      // Add hero image (required for new cards, optional for updates)
      if (formData.hero_image) {
        formDataToSend.append('hero_image', formData.hero_image);
      }
      
      // Send color_variants_data as JSON string (now includes price)
      const colorVariantsData = formData.available_colours.map(colour_id => {
        const variantData = colorVariantImages[colour_id] || {};
        return {
          colour_id,
          price: variantData.price || 0
        };
      });
      formDataToSend.append('color_variants_data', JSON.stringify(colorVariantsData));

      const method = editingCard ? 'PUT' : 'POST';
      const url = editingCard 
        ? `${BACKEND_URL}/api/admin/digital-cards/${editingCard.id}`
        : `${BACKEND_URL}/api/admin/digital-cards`;

      // Create/Update card
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save card');
      }

      const savedCard = await response.json();
      const cardId = savedCard.id;

      // Step 2: Upload images for each color variant
      for (const colour_id of formData.available_colours) {
        const variantImages = colorVariantImages[colour_id];
        
        if (variantImages) {
          // Upload front image if it's a File object (new upload)
          if (variantImages.front instanceof File) {
            const frontFormData = new FormData();
            frontFormData.append('colour_id', colour_id);
            frontFormData.append('side', 'front');
            frontFormData.append('image', variantImages.front);

            await fetch(`${BACKEND_URL}/api/admin/digital-cards/${cardId}/upload-variant-image`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
              body: frontFormData
            });
          }

          // Upload back image if it's a File object (new upload)
          if (variantImages.back instanceof File) {
            const backFormData = new FormData();
            backFormData.append('colour_id', colour_id);
            backFormData.append('side', 'back');
            backFormData.append('image', variantImages.back);

            await fetch(`${BACKEND_URL}/api/admin/digital-cards/${cardId}/upload-variant-image`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
              body: backFormData
            });
          }
        }
      }

      setShowModal(false);
      setEditingCard(null);
      resetForm();
      fetchCards();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/digital-cards/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete card');
      }

      fetchCards();
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal = (card = null) => {
    if (card) {
      setEditingCard(card);
      setFormData({
        name: card.name,
        short_description: card.short_description,
        card_category_id: card.card_category_id || '',
        available_colours: card.color_variants ? card.color_variants.map(v => v.colour_id) : [],
        hero_image: null
      });
      
      // Set hero image preview
      if (card.hero_image) {
        setHeroImagePreview(`${BACKEND_URL}${card.hero_image}`);
      }
      
      // Load existing color variant images and prices
      if (card.color_variants) {
        const variantImages = {};
        card.color_variants.forEach(variant => {
          variantImages[variant.colour_id] = {
            front: variant.front_image ? `${BACKEND_URL}${variant.front_image}` : null,
            back: variant.back_image ? `${BACKEND_URL}${variant.back_image}` : null,
            price: variant.price || 0
          };
        });
        setColorVariantImages(variantImages);
      }
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      short_description: '',
      card_category_id: '',
      available_colours: [],
      hero_image: null
    });
    setHeroImagePreview(null);
    setColorVariantImages({});
    setEditingCard(null);
    setError('');
  };

  const handleColorImageUpload = (colour_id, side, file) => {
    setColorVariantImages(prev => ({
      ...prev,
      [colour_id]: {
        ...prev[colour_id],
        [side]: file
      }
    }));
  };

  const handleColorPriceChange = (colour_id, price) => {
    setColorVariantImages(prev => ({
      ...prev,
      [colour_id]: {
        ...prev[colour_id],
        price: parseFloat(price) || 0
      }
    }));
  };

  const getImagePreview = (colour_id, side) => {
    const variantImages = colorVariantImages[colour_id];
    if (!variantImages || !variantImages[side]) return null;
    
    // If it's a File object, create object URL
    if (variantImages[side] instanceof File) {
      return URL.createObjectURL(variantImages[side]);
    }
    
    // If it's already a URL string
    return variantImages[side];
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading digital cards...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-navy">Digital Cards</h1>
              <p className="text-gray-600">Manage your digital business cards</p>
            </div>
          </div>
          <Button
            onClick={() => openModal()}
            className="bg-navy text-white hover:bg-navy/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {cards.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {cards.map((card) => (
              <div key={card.id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-bold text-navy">{card.name}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openModal(card)}
                        className="text-navy hover:text-navy/80"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(card.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{card.short_description}</p>
                  
                  {/* Category Info */}
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500">Category</p>
                    <p className="text-sm font-semibold text-navy">
                      {card.card_category_id ? (
                        <>
                          {cardCategories.find(cat => cat.id === card.card_category_id)?.name || 
                           `Unknown (ID: ${card.card_category_id})`}
                        </>
                      ) : (
                        <span className="text-red-600">No Category Assigned</span>
                      )}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Hero Image */}
                    {card.hero_image && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Hero Image</p>
                        <div className="w-full" style={{ aspectRatio: '1.586' }}>
                          <img 
                            src={`${BACKEND_URL}${card.hero_image}`}
                            alt="Hero"
                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      </div>
                    )}

                    {/* Color Variants with Prices */}
                    {card.color_variants && card.color_variants.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Available Colors & Prices</p>
                        <div className="flex flex-wrap gap-2">
                          {card.color_variants.map((variant) => (
                            <div key={variant.colour_id} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200">
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-300"
                                style={{ backgroundColor: variant.colour_code }}
                              ></div>
                              <span className="text-xs text-gray-700">{variant.colour_name}</span>
                              <span className="text-xs font-semibold text-navy ml-1">${variant.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Color Variant Images Grid */}
                    {card.color_variants && card.color_variants.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Variant Designs</p>
                        <div className="grid grid-cols-2 gap-3">
                          {card.color_variants.slice(0, 2).map((variant) => (
                            <div key={variant.colour_id}>
                              <p className="text-xs text-gray-500 mb-1">{variant.colour_name}</p>
                              <div className="grid grid-cols-2 gap-2">
                                {variant.front_image && (
                                  <div className="w-full" style={{ aspectRatio: '1.586' }}>
                                    <img 
                                      src={`${BACKEND_URL}${variant.front_image}`}
                                      alt={`${variant.colour_name} front`}
                                      className="w-full h-full object-cover rounded border border-gray-200"
                                    />
                                  </div>
                                )}
                                {variant.back_image && (
                                  <div className="w-full" style={{ aspectRatio: '1.586' }}>
                                    <img 
                                      src={`${BACKEND_URL}${variant.back_image}`}
                                      alt={`${variant.colour_name} back`}
                                      className="w-full h-full object-cover rounded border border-gray-200"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {card.color_variants.length > 2 && (
                          <p className="text-xs text-gray-500 mt-2">+ {card.color_variants.length - 2} more variants</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Digital Cards Yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first digital card to get started
            </p>
            <Button
              onClick={() => openModal()}
              className="bg-navy text-white hover:bg-navy/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Card
            </Button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-navy">
                  {editingCard ? 'Edit' : 'Add'} Digital Card
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Card Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="e.g., Premium Business Card"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Short Description *
                  </label>
                  <textarea
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    rows="3"
                    placeholder="Brief description of the card"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Card Category
                  </label>
                  {editingCard && formData.card_category_id && !cardCategories.find(cat => cat.id === formData.card_category_id) && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-2 text-sm">
                      ⚠️ Warning: Current category ID ({formData.card_category_id}) doesn't exist! Please select a valid category.
                    </div>
                  )}
                  <select
                    value={formData.card_category_id}
                    onChange={(e) => setFormData({ ...formData, card_category_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    {cardCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Material *
                  </label>
                  <select
                    value={formData.material_id}
                    onChange={(e) => setFormData({ ...formData, material_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  >
                    <option value="">Select a material</option>
                    {materials.map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Hero Image {!editingCard && '*'}
                  </label>
                  <p className="text-xs text-gray-600 mb-2">This image will be shown in the card listings page</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setFormData({ ...formData, hero_image: e.target.files[0] });
                        setHeroImagePreview(URL.createObjectURL(e.target.files[0]));
                      }
                    }}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required={!editingCard}
                  />
                  {heroImagePreview && (
                    <div className="mt-2 w-full" style={{ aspectRatio: '1.586' }}>
                      <img 
                        src={heroImagePreview}
                        alt="Hero preview"
                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Available Colours
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {colours.length > 0 ? (
                      colours.map((colour) => (
                        <label key={colour.id} className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.available_colours.includes(colour.id)}
                            onChange={(e) => {
                              const newColours = e.target.checked
                                ? [...formData.available_colours, colour.id]
                                : formData.available_colours.filter(id => id !== colour.id);
                              setFormData({ ...formData, available_colours: newColours });
                            }}
                            className="w-4 h-4 text-navy focus:ring-navy rounded"
                          />
                          <div 
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: colour.code }}
                          ></div>
                          <span className="text-sm">{colour.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No colours available</p>
                    )}
                  </div>
                </div>

                {/* Color Variant Image Uploads */}
                {formData.available_colours.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-semibold text-navy mb-4">Upload Images Per Color</h3>
                    <div className="space-y-6">
                      {formData.available_colours.map((colour_id) => {
                        const colour = colours.find(c => c.id === colour_id);
                        return (
                          <div key={colour_id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-center gap-2 mb-3">
                              <div 
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: colour?.code }}
                              ></div>
                              <h4 className="font-semibold text-navy">{colour?.name}</h4>
                            </div>
                            
                            {/* Price Field */}
                            <div className="mb-4">
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Price (USD) *
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={colorVariantImages[colour_id]?.price || ''}
                                onChange={(e) => handleColorPriceChange(colour_id, e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                                placeholder="49.99"
                                required
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              {/* Front Image */}
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                  Front Image *
                                </label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      handleColorImageUpload(colour_id, 'front', e.target.files[0]);
                                    }
                                  }}
                                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                                  required={!editingCard && !getImagePreview(colour_id, 'front')}
                                />
                                {getImagePreview(colour_id, 'front') && (
                                  <div className="mt-2" style={{ aspectRatio: '1.586' }}>
                                    <img 
                                      src={getImagePreview(colour_id, 'front')}
                                      alt={`${colour?.name} front`}
                                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Back Image */}
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                  Back Image *
                                </label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      handleColorImageUpload(colour_id, 'back', e.target.files[0]);
                                    }
                                  }}
                                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                                  required={!editingCard && !getImagePreview(colour_id, 'back')}
                                />
                                {getImagePreview(colour_id, 'back') && (
                                  <div className="mt-2" style={{ aspectRatio: '1.586' }}>
                                    <img 
                                      src={getImagePreview(colour_id, 'back')}
                                      alt={`${colour?.name} back`}
                                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-navy text-white hover:bg-navy/90"
                  >
                    {submitting ? 'Saving...' : editingCard ? 'Update Card' : 'Create Card'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
